import { Alert, Button, Checkbox } from "@digdir/designsystemet-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { GetAvtalePrivatResponse } from "@/api/generated";
import {
  getAvtalePrivatOptions,
  getAvtalePrivatQueryKey,
  getAvtalerQueryKey,
  postAvbestillAvtaleMutation,
} from "@/api/generated/@tanstack/react-query.gen";
import { DescriptionList, DescriptionTerm, Page } from "@/components";
import { useToaster } from "@/features/toaster";
import {
  dateHasPassed,
  formatDateString,
  formatDateStringInterval,
  formatPostnummer,
} from "@/utils";
import { AvtaleSectionWithHeader } from "../common/AvtaleSectionWithHeader";
import { AvbestillBekreftelseManglerFeil } from "./AvbestillBekreftelseManglerFeil";
import { AvbestillDetaljerContainer } from "./AvbestillDetaljerContainer";
import { AvbestillFeilet } from "./AvbestillFeilet";
import { AvbestillHeader } from "./AvbestillHeader";
import { AvbestillingsFristUtloptAlert } from "./AvbestillingsFristUtloptAlert";
import { AvbestillLoadingSkeleton } from "./AvbestillLoadingSkeleton";
import { AvtaleAvbestiltAlert } from "./AvtaleAvbestiltAlert";
import { AvtaleUtloptAlert } from "./AvtaleUtloptAlert";

export const AvbestillPrivatAvtalePage = () => {
  const { t } = useTranslation();
  const params = useParams({
    from: "/_app/avtaler/$avtalerId/privat/avbestill/",
  });

  const {
    data: privatAvtale,
    isLoading: isLoadingPrivatAvtale,
    isError: isErrorPrivatAvtale,
  } = useQuery({
    ...getAvtalePrivatOptions({
      client: httpClient,
      path: {
        id: params.avtalerId,
      },
    }),
  });

  return (
    <Page withBackLink>
      <div className="flex flex-col gap-6 max-w-2xl">
        <AvbestillHeader />

        {isLoadingPrivatAvtale ? (
          <AvbestillLoadingSkeleton />
        ) : isErrorPrivatAvtale ? (
          <Alert data-color={"danger"}>
            {t("pages.avtaler.avbestilling.feil-lasting")}
          </Alert>
        ) : (
          <PrivatAvtaleDetaljer privatAvtale={privatAvtale} />
        )}
      </div>
    </Page>
  );
};

const PrivatAvtaleDetaljer = ({
  privatAvtale,
}: {
  privatAvtale: GetAvtalePrivatResponse | undefined;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const toast = useToaster();
  const queryClient = useQueryClient();

  const postAvbestillMutation = useMutation({
    ...postAvbestillAvtaleMutation({
      client: httpClient,
    }),
    onError: (_, __, ___) => {
      setAvbestillingFeilet(true);
    },
    onSuccess: (response, options, __) => {
      queryClient.invalidateQueries({
        queryKey: getAvtalePrivatQueryKey({
          path: { id: options.path.id },
          client: httpClient,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getAvtalerQueryKey({
          client: httpClient,
        }),
      });
      const footer =
        response.avtale.cancelledAt != null &&
        formatDateString(response.avtale.cancelledAt);
      toast.success({
        messageKey: "pages.avtaler.avbestilling.alert-vellykket-beskrivelse",
        titleKey: "pages.avtaler.avbestilling.alert-vellykket-tittel",
        footer,
      });

      router.navigate({ to: "/avtaler" });
    },
  });

  const [bekreftAvbestilling, setBekreftAvbestilling] = useState(false);
  const [avbestillingBekreftet, setAvbestillingBekreftet] = useState(false);
  const [avbestillingFeilet, setAvbestillingFeilet] = useState(false);

  const handleAvbestill = () => {
    setAvbestillingFeilet(false);
    if (bekreftAvbestilling !== true) {
      setAvbestillingBekreftet(true);
      return;
    }

    setAvbestillingBekreftet(false);

    const avtaleId = privatAvtale?.avtale.avtaleId;
    if (avtaleId == null) return;
    postAvbestillMutation.mutate({ body: {}, path: { id: avtaleId } });
  };

  const handleAvbryt = () => {
    router.navigate({ to: "/" });
  };
  return (
    <AvbestillDetaljerContainer>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.leverandør-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avbestilling.leverandør-navn"
            value={privatAvtale?.avtale.leverandør.name}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.bestiller-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.bestiller-navn"
            value={privatAvtale?.avtale.bestiller.navn}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.kundeNummer"
            value={privatAvtale?.avtale.kundenummer}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.avtaleperiode-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avbestilling.avtaleperiode-dato"
            value={`${formatDateStringInterval(privatAvtale?.avtale.activeFromInclusive, privatAvtale?.avtale.activeToExclusive)}`}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      <AvtaleSectionWithHeader
        title={t("pages.avtaler.avbestilling.informasjon-privat-tittel")}
      >
        <DescriptionList>
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.typeBolig"
            value={t("enums.boligType", {
              context:
                privatAvtale?.avtale.avtaleDetaljerPrivat?.boligType ??
                "Ukjent",
            })}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.adresse"
            value={`${privatAvtale?.avtale.avtaleDetaljerPrivat?.adresse}, ${formatPostnummer(privatAvtale?.avtale.avtaleDetaljerPrivat?.postnummer)} ${privatAvtale?.avtale.avtaleDetaljerPrivat?.sted}`}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.målerId"
            value={privatAvtale?.avtale.målerId}
          />
        </DescriptionList>
      </AvtaleSectionWithHeader>
      {privatAvtale?.avtale.avtaleStatus === "Avbestilt" ? (
        <AvtaleAvbestiltAlert avtale={privatAvtale.avtale} />
      ) : privatAvtale?.avtale.avtaleStatus === "Utgått" ? (
        <AvtaleUtloptAlert avtale={privatAvtale.avtale} />
      ) : privatAvtale?.avtale.avbestillingsFrist != null &&
        dateHasPassed(privatAvtale.avtale.avbestillingsFrist) ? (
        <AvbestillingsFristUtloptAlert avtale={privatAvtale.avtale} />
      ) : (
        <AvtaleSectionWithHeader
          title={t("pages.avtaler.avbestilling.bekreft-avbestilling-tittel")}
          className="space-y-4"
        >
          <Checkbox
            label={t(
              "pages.avtaler.avbestilling.bekreft-avbestilling-checkbox",
            )}
            checked={bekreftAvbestilling}
            onChange={() => setBekreftAvbestilling(!bekreftAvbestilling)}
          />

          {avbestillingBekreftet && <AvbestillBekreftelseManglerFeil />}
          {avbestillingFeilet && <AvbestillFeilet />}

          <div className="flex gap-6">
            <Button variant="secondary" onClick={handleAvbryt}>
              {t("pages.avtaler.avbestilling.knapp-avbryt")}
            </Button>
            <Button
              variant="primary"
              data-color={"danger"}
              onClick={handleAvbestill}
              loading={postAvbestillMutation.isPending}
            >
              {t("pages.avtaler.avbestilling.knapp-avbestill")}
            </Button>
          </div>
        </AvtaleSectionWithHeader>
      )}
    </AvbestillDetaljerContainer>
  );
};
